import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {QuestionnaireEditor} from '../../types';
import {KEYS} from '../../translation-keys';

/** Describes an insertable value from the questionnaire runtime context. */
export interface TemplateVariable {
  path: string;
  label: string;
  group: string;
  template: string;
  questionContext?: {
    questionId: string;
    questionTitle: string;
    label: string;
    ancestors: string[];
  };
}

/** Describes a translated group in the variable picker. */
export interface TemplateVariableGroup {
  id: string;
  label: string;
  icon: string;
}

/** Lists only scopes available when questionnaire text is rendered. */
export const TEMPLATE_VARIABLE_GROUPS: TemplateVariableGroup[] = [
  {id: 'client', label: KEYS.EDITOR.TEXT_VARIABLE_PATIENT, icon: 'fa-user-o'},
  {id: 'custom', label: KEYS.EDITOR.TEXT_VARIABLE_CUSTOM, icon: 'fa-id-card-o'},
  {id: 'inline', label: KEYS.EDITOR.TEXT_VARIABLE_ANSWERS, icon: 'fa-list-ul'},
  {id: 'scoring', label: KEYS.EDITOR.TEXT_VARIABLE_SCORES, icon: 'fa-bar-chart'},
  {id: 'formula', label: KEYS.EDITOR.TEXT_VARIABLE_FORMULAS, icon: 'fa-calculator'},
  {id: 'extern', label: KEYS.EDITOR.TEXT_VARIABLE_EXTERNAL, icon: 'fa-exchange'},
  {id: 'general', label: KEYS.EDITOR.TEXT_VARIABLE_GENERAL, icon: 'fa-clock-o'}
];

/** Converts authored titles to plain labels without interpreting their markup. */
function plainLabel(html: string): string {
  const document = new DOMParser().parseFromString(html || '', 'text/html');
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Builds the picker from current mappings, including configured patient custom fields. */
export function buildTemplateVariables(
  questionnaire: Questionnaire,
  scopes: QuestionnaireEditor.VariableScopes,
  locale: string,
  translate: (label: string) => string
): TemplateVariable[] {
  const result = new Map<string, TemplateVariable>();
  const containerLabels = new Map<string, string>();
  const containerContexts = new Map<string, TemplateVariable['questionContext']>();
  const variables = questionnaire?.meta?.options?.variables || {};

  /** Indexes current containers so labels also reflect unsaved title edits. */
  const indexContainer = (container: Questionnaire.Container, question: Questionnaire.Container,
    questionTitle: string, parentLabel = '', ancestors: string[] = []): void => {
    const title = plainLabel(container.title?.[locale] || container.title?.de_DE);
    const label = title && parentLabel && title !== parentLabel ? `${parentLabel} › ${title}` : title || parentLabel;
    container.pathHash && containerLabels.set(container.pathHash, label);
    if (container.pathHash) {
      containerContexts.set(container.pathHash, {questionId: question.pathHash, questionTitle,
        label: title || label, ancestors});
    }
    const nextAncestors = container !== question && !container.omitContainer && title ? [...ancestors, title] : ancestors;
    (container.elements || []).forEach(child => indexContainer(child, question, questionTitle, label, nextAncestors));
  };
  (questionnaire?.questions || []).forEach((question, index) => indexContainer(question, question,
    plainLabel(question.title?.[locale] || question.title?.de_DE) || `${translate('Frage')} ${index + 1}`));

  /** Adds one runtime path once and keeps unsafe names out of generated expressions. */
  const add = (path: string, label: string, group: string): void => {
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_-]*)*$/.test(path)) {
      return;
    }
    result.set(path, {path, label: label || path, group, template: `{{${path}}}`});
  };

  const patientLabels = {
    firstName: KEYS.EDITOR.TEXT_VARIABLE_FIRST_NAME,
    lastName: KEYS.EDITOR.TEXT_VARIABLE_LAST_NAME,
    name: KEYS.EDITOR.TEXT_VARIABLE_FULL_NAME,
    salutation: KEYS.EDITOR.TEXT_VARIABLE_SALUTATION,
    birthDate: KEYS.EDITOR.TEXT_VARIABLE_BIRTH_DATE,
    age: KEYS.EDITOR.TEXT_VARIABLE_AGE,
    ageInMonths: KEYS.EDITOR.TEXT_VARIABLE_AGE_MONTHS,
    anonymous: KEYS.EDITOR.TEXT_VARIABLE_ANONYMOUS,
    pseudonymous: KEYS.EDITOR.TEXT_VARIABLE_PSEUDONYMOUS
  };
  const mappings = scopes?.client?.mappings || {};
  Object.keys(mappings).forEach(label => {
    const name = mappings[label];
    const custom = name.indexOf('custom.') === 0;
    add(`client.${name}`, custom ? label.replace(/ \(custom\)$/, '') : translate(label), custom ? 'custom' : 'client');
  });
  Object.keys(patientLabels).forEach(name => add(`client.${name}`, translate(patientLabels[name]), 'client'));

  ['inline', 'scoring', 'formula', 'extern'].forEach(scope => {
    Object.keys(variables[scope] || {}).forEach(name => {
      const mapping = variables[scope][name];
      const label = scope === 'inline' && containerLabels.get(Object.keys(mapping || {})[0]);
      add(`${scope}.${name}`, label || name, scope);
      const variable = result.get(`${scope}.${name}`);
      if (scope === 'inline' && variable) {
        variable.questionContext = containerContexts.get(Object.keys(mapping || {})[0]);
      }
    });
  });
  add('date', translate(KEYS.EDITOR.TEXT_VARIABLE_DATE), 'general');
  add('time', translate(KEYS.EDITOR.TEXT_VARIABLE_TIME), 'general');
  return Array.from(result.values());
}
